import React, { useState, useEffect } from "react";
import { supabase } from "../config/supabase";
import { fireDataRefresh } from "../utils/events";
import { User } from "@supabase/supabase-js";
import {
  sanitizeError,
  isValidPassword,
  getPasswordValidationError,
} from "../utils/security";
import ChildManagement from "./ChildManagement";
import ConfirmationModal from "../components/ConfirmationModal";

interface SettingsProps {
  user: User | null;
  profile: any;
  onProfileUpdate: (profile: any) => void;
  onLogout: () => void;
  darkMode: boolean;
  onDarkModeToggle: () => void;
  initialTab?:
    | "profile"
    | "children"
    | "goals"
    | "security"
    | "appearance"
    | "danger";
}

const Settings: React.FC<SettingsProps> = ({
  user,
  profile,
  onProfileUpdate,
  onLogout,
  darkMode,
  onDarkModeToggle,
  initialTab = "profile",
}) => {
  const [activeTab, setActiveTab] = useState<
    "profile" | "children" | "goals" | "security" | "appearance" | "danger"
  >(initialTab);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [childrenCount, setChildrenCount] = useState(0);
  const [statusTimeout, setStatusTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

  // Check if user is a doctor
  const isDoctor = profile?.account_type === "doctor";
  const isAdmin = profile?.account_type === "admin";

  // Helper function to process name input (capitalize and remove numbers)
  const processNameInput = (value: string): string => {
    // Remove any numbers and special characters except spaces, hyphens, and apostrophes
    const cleaned = value.replace(/[^A-Za-z\s\-']/g, "");
    // Capitalize first letter of each word
    const result = cleaned
      .split(" ")
      .map((word) => {
        if (word.length === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");

    return result;
  };

  // Check if profile form has changes
  const hasProfileChanges = () => {
    return (
      profileForm.name.trim() !== originalProfile.name.trim() ||
      profileForm.surname.trim() !== originalProfile.surname.trim() ||
      profileForm.info.trim() !== originalProfile.info.trim() ||
      profileForm.treatmentType !== originalProfile.treatmentType
    );
  };

  // Update original profile when profile changes
  useEffect(() => {
    if (profile) {
      const newOriginal = {
        name: profile.name || "",
        surname: profile.surname || "",
        info: profile.info || "",
        treatmentType: (profile.treatment_type as "self" | "child") || "self",
      };
      setOriginalProfile(newOriginal);
      setProfileForm(newOriginal);
      setTreatmentTypeChanged(false); // Reset changed flag when profile loads
    }
  }, [profile]);

  // Function to set status with auto-dismiss
  const setStatusWithTimeout = (message: string, duration: number = 3000) => {
    // Clear existing timeout
    if (statusTimeout) {
      clearTimeout(statusTimeout);
    }

    setStatus(message);

    // Set new timeout
    const timeout = setTimeout(() => {
      setStatus("");
      setStatusTimeout(null);
    }, duration);

    setStatusTimeout(timeout);
  };

  // Clear status when clicking elsewhere
  const clearStatus = () => {
    if (statusTimeout) {
      clearTimeout(statusTimeout);
      setStatusTimeout(null);
    }
    setStatus("");
  };

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: profile?.name || "",
    surname: profile?.surname || "",
    info: profile?.info || "",
    treatmentType: (profile?.treatment_type as "self" | "child") || "self",
  });
  const [originalProfile, setOriginalProfile] = useState({
    name: profile?.name || "",
    surname: profile?.surname || "",
    info: profile?.info || "",
    treatmentType: (profile?.treatment_type as "self" | "child") || "self",
  });
  const [treatmentTypeChanged, setTreatmentTypeChanged] = useState(false);

  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Goals form state
  const [goalsForm, setGoalsForm] = useState({
    // Weekly goal is auto-calculated in the backend from daily goals
    monday_goal_minutes: null as number | null,
    tuesday_goal_minutes: null as number | null,
    wednesday_goal_minutes: null as number | null,
    thursday_goal_minutes: null as number | null,
    friday_goal_minutes: null as number | null,
    saturday_goal_minutes: null as number | null,
    sunday_goal_minutes: null as number | null,
  });

  // Original goals to track changes
  const [originalGoals, setOriginalGoals] = useState({
    monday_goal_minutes: null as number | null,
    tuesday_goal_minutes: null as number | null,
    wednesday_goal_minutes: null as number | null,
    thursday_goal_minutes: null as number | null,
    friday_goal_minutes: null as number | null,
    saturday_goal_minutes: null as number | null,
    sunday_goal_minutes: null as number | null,
  });

  // Reset treatment type changed flag when treatment type is back to original
  useEffect(() => {
    if (profileForm.treatmentType === originalProfile.treatmentType) {
      setTreatmentTypeChanged(false);
    }
  }, [profileForm.treatmentType, originalProfile.treatmentType]);

  // Switch away from goals tab if treatment type changes to "child" (only after save)
  // This should only trigger when the actual saved profile changes, not form state
  useEffect(() => {
    const savedTreatmentType =
      profile?.treatment_type || originalProfile.treatmentType;
    if (savedTreatmentType === "child" && activeTab === "goals") {
      setActiveTab("children"); // Switch to children tab if goals tab is active
    }
  }, [profile?.treatment_type, originalProfile.treatmentType, activeTab]);

  // Fetch goals on component mount
  React.useEffect(() => {
    fetchGoals();
  }, [user]);

  const fetchGoals = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("user_goals")
        .select(
          "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
        )
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        const goals = {
          monday_goal_minutes:
            (data as any).monday_goal_minutes ?? null,
          tuesday_goal_minutes:
            (data as any).tuesday_goal_minutes ?? null,
          wednesday_goal_minutes:
            (data as any).wednesday_goal_minutes ?? null,
          thursday_goal_minutes:
            (data as any).thursday_goal_minutes ?? null,
          friday_goal_minutes:
            (data as any).friday_goal_minutes ?? null,
          saturday_goal_minutes:
            (data as any).saturday_goal_minutes ?? null,
          sunday_goal_minutes:
            (data as any).sunday_goal_minutes ?? null,
        };
        setGoalsForm(goals);
        setOriginalGoals(goals);
      } else {
        // If no data, use defaults (null = rest day)
        const defaultGoals = {
          monday_goal_minutes: null as number | null,
          tuesday_goal_minutes: null as number | null,
          wednesday_goal_minutes: null as number | null,
          thursday_goal_minutes: null as number | null,
          friday_goal_minutes: null as number | null,
          saturday_goal_minutes: null as number | null,
          sunday_goal_minutes: null as number | null,
        };
        setGoalsForm(defaultGoals);
        setOriginalGoals(defaultGoals);
      }
    } catch (error) {
      console.error("Error fetching goals:", error);
    }
  };

  // Fetch children count on component mount
  React.useEffect(() => {
    fetchChildrenCount();
  }, [user]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (statusTimeout) {
        clearTimeout(statusTimeout);
      }
    };
  }, [statusTimeout]);

  const fetchChildrenCount = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("children")
        .select("id")
        .eq("user_id", user.id);

      if (error) throw error;

      setChildrenCount(data?.length || 0);
    } catch (error) {
      console.error("Error fetching children count:", error);
    }
  };

  // Input validation
  const validateName = (name: string): boolean => {
    return name.trim().length >= 2 && /^[A-Za-z\s\-']+$/.test(name.trim());
  };

  // Remove weak password validation - use isValidPassword from security utils

  // Profile update handler
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    if (!validateName(profileForm.name)) {
      setStatus(
        "Name must be at least 2 characters and contain only letters. Numbers are not allowed.",
      );
      setLoading(false);
      return;
    }

    if (!validateName(profileForm.surname)) {
      setStatus(
        "Surname must be at least 2 characters and contain only letters. Numbers are not allowed.",
      );
      setLoading(false);
      return;
    }

    try {
      if (!user) throw new Error("No user");

      // Prepare update data
      const updateData: any = {
        name: profileForm.name.trim(),
        surname: profileForm.surname.trim(),
        treatment_type: profileForm.treatmentType,
      };

      // Only include info field if user is a doctor
      if (isDoctor) {
        updateData.info = profileForm.info.trim();
      }

      const { error } = await supabase
        .from("user_data")
        .update(updateData)
        .eq("user_id", user.id);

      if (error) throw error;

      setStatusWithTimeout("Profile updated successfully!");

      // Update original profile to reflect the changes
      setOriginalProfile({
        name: profileForm.name.trim(),
        surname: profileForm.surname.trim(),
        info: profileForm.info.trim(),
        treatmentType: profileForm.treatmentType,
      });
      // Reset treatment type changed flag
      setTreatmentTypeChanged(false);

      fireDataRefresh({ reason: "profile_updated" });

      // Update parent component
      const updatedProfile = {
        ...profile,
        name: profileForm.name.trim(),
        surname: profileForm.surname.trim(),
        treatment_type: profileForm.treatmentType,
        ...(isDoctor && { info: profileForm.info.trim() }),
      };
      onProfileUpdate(updatedProfile);
    } catch (err: any) {
      console.error("Profile update error:", err);
      setStatus("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoalsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    const dayKeys = [
      "monday_goal_minutes",
      "tuesday_goal_minutes",
      "wednesday_goal_minutes",
      "thursday_goal_minutes",
      "friday_goal_minutes",
      "saturday_goal_minutes",
      "sunday_goal_minutes",
    ] as const;

    for (const k of dayKeys) {
      const v = goalsForm[k];
      if (v === null) continue; // rest day
      if (v === 0) continue; // will be saved as NULL
      if (v < 30) {
        setStatus("Daily goals must be 30+ minutes (or 0 for rest day).");
        setLoading(false);
        return;
      }
      if (v > 480) {
        setStatus("Daily goals must be 480 minutes (8 hours) or less.");
        setLoading(false);
        return;
      }
    }

    try {
      if (!user) throw new Error("No user");

      const { error } = await supabase.from("user_goals").upsert(
        {
          user_id: user.id,
          // Save per-day goals; store rest day as NULL (user enters 0)
          monday_goal_minutes:
            goalsForm.monday_goal_minutes === 0
              ? null
              : goalsForm.monday_goal_minutes,
          tuesday_goal_minutes:
            goalsForm.tuesday_goal_minutes === 0
              ? null
              : goalsForm.tuesday_goal_minutes,
          wednesday_goal_minutes:
            goalsForm.wednesday_goal_minutes === 0
              ? null
              : goalsForm.wednesday_goal_minutes,
          thursday_goal_minutes:
            goalsForm.thursday_goal_minutes === 0
              ? null
              : goalsForm.thursday_goal_minutes,
          friday_goal_minutes:
            goalsForm.friday_goal_minutes === 0
              ? null
              : goalsForm.friday_goal_minutes,
          saturday_goal_minutes:
            goalsForm.saturday_goal_minutes === 0
              ? null
              : goalsForm.saturday_goal_minutes,
          sunday_goal_minutes:
            goalsForm.sunday_goal_minutes === 0
              ? null
              : goalsForm.sunday_goal_minutes,
        },
        { onConflict: "user_id" },
      );

      if (error) throw error;

      // Snapshot goals into history so past rest days are preserved accurately
      await supabase.from("goal_history").upsert({
        user_id: user.id,
        child_id: null,
        effective_from: new Date().toISOString().split("T")[0],
        sunday_goal_minutes: goalsForm.sunday_goal_minutes === 0 ? null : goalsForm.sunday_goal_minutes,
        monday_goal_minutes: goalsForm.monday_goal_minutes === 0 ? null : goalsForm.monday_goal_minutes,
        tuesday_goal_minutes: goalsForm.tuesday_goal_minutes === 0 ? null : goalsForm.tuesday_goal_minutes,
        wednesday_goal_minutes: goalsForm.wednesday_goal_minutes === 0 ? null : goalsForm.wednesday_goal_minutes,
        thursday_goal_minutes: goalsForm.thursday_goal_minutes === 0 ? null : goalsForm.thursday_goal_minutes,
        friday_goal_minutes: goalsForm.friday_goal_minutes === 0 ? null : goalsForm.friday_goal_minutes,
        saturday_goal_minutes: goalsForm.saturday_goal_minutes === 0 ? null : goalsForm.saturday_goal_minutes,
      }, { onConflict: "user_id,child_id,effective_from" });

      // Update original goals to match current form values
      setOriginalGoals({
        monday_goal_minutes: goalsForm.monday_goal_minutes,
        tuesday_goal_minutes: goalsForm.tuesday_goal_minutes,
        wednesday_goal_minutes: goalsForm.wednesday_goal_minutes,
        thursday_goal_minutes: goalsForm.thursday_goal_minutes,
        friday_goal_minutes: goalsForm.friday_goal_minutes,
        saturday_goal_minutes: goalsForm.saturday_goal_minutes,
        sunday_goal_minutes: goalsForm.sunday_goal_minutes,
      });

      setStatusWithTimeout("Goals updated successfully!");
      fireDataRefresh({ reason: "goals_updated" });
    } catch (error: any) {
      const safeError = sanitizeError(error);
      setStatus(`Failed to update goals: ${safeError}`);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced password validation
  const validatePasswordStrength = isValidPassword;

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    if (!passwordForm.currentPassword) {
      setStatus("Current password is required.");
      setLoading(false);
      return;
    }

    if (!validatePasswordStrength(passwordForm.newPassword)) {
      const passwordError = getPasswordValidationError(
        passwordForm.newPassword,
      );
      setStatus(passwordError || "Password does not meet requirements.");
      setLoading(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setStatus("New passwords do not match.");
      setLoading(false);
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setStatus("New password must be different from current password.");
      setLoading(false);
      return;
    }

    try {
      // Verify current password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || "",
        password: passwordForm.currentPassword,
      });

      if (signInError) {
        setStatus("Current password is incorrect.");
        setLoading(false);
        return;
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });

      if (error) throw error;

      setStatusWithTimeout("Password updated successfully!");
      fireDataRefresh({ reason: "password_updated" });
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setStatus(`Failed to update password: ${safeError}`);
    } finally {
      setLoading(false);
    }
  };

  // Account deletion handler
  const handleDeleteAccountClick = () => {
    setShowDeleteAccountModal(true);
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    setStatus("");
    setShowDeleteAccountModal(false);

    try {
      // Use the server-side function to completely delete the user account
      // This deletes all data AND the auth record
      const { data, error } = await supabase.rpc("delete_user_completely");

      if (error) throw error;

      // Check the response from the function
      if (data && data.success) {
        setStatus(
          "Account completely deleted. You will be signed out in 3 seconds.",
        );
        setTimeout(() => {
          onLogout();
        }, 3000);
      } else {
        // Handle function-level errors
        const errorMessage = data?.message || "Unknown error occurred";
        setStatus(`Account deletion failed: ${errorMessage}`);
      }
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setStatus(`Failed to delete account: ${safeError}`);
    } finally {
      setLoading(false);
    }
  };

  // Use saved profile value for tabs, not unsaved form value
  const currentTreatmentType =
    profile?.treatment_type || originalProfile.treatmentType;

  const tabs = [
    { id: "profile", name: "Profile", icon: "👤" },
    ...(!isAdmin && !isDoctor && currentTreatmentType === "child"
      ? [{ id: "children", name: "Children", icon: "👶" }]
      : []),
    // Show goals tab only for self treatment users (not for child treatment)
    ...(!isAdmin && !isDoctor && currentTreatmentType === "self"
      ? [{ id: "goals", name: "Goals", icon: "🎯" }]
      : []),
    { id: "security", name: "Security", icon: "🔒" },
    { id: "appearance", name: "Appearance", icon: "🎨" },
    { id: "danger", name: "Danger Zone", icon: "⚠️" },
  ];

  return (
    <div className="flex items-center justify-center w-full">
      {/* Main Settings Card */}
      <div
        // Use min-height so outer page/main scroll is used, not the inner card
        className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl p-[30px] flex flex-col items-center space-y-6 ${
          darkMode
            ? "bg-gray-800 shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
            : "bg-white shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
        }`}
        onClick={(e) => {
          // Only clear status if clicking on the container itself, not on child elements
          if (e.target === e.currentTarget) {
            clearStatus();
          }
        }}
      >
        {/* Title and Description inside the card */}
        <div className="text-center mb-4 pt-6 px-6">
          <h1
            className={`text-3xl font-bold mb-2 ${
              darkMode ? "text-white" : "text-brand-dark-blue"
            }`}
          >
            Settings
          </h1>
          <p className={`${darkMode ? "text-gray-300" : "text-gray-600"}`}>
            Manage your account preferences and security
          </p>
        </div>
        {/* Tab Navigation */}
        <div
          className={`flex flex-wrap border-b ${
            darkMode ? "border-gray-600" : "border-gray-200"
          }`}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 min-w-0 py-4 px-6 text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-brand-dark-blue text-white shadow-lg"
                  : darkMode
                    ? "bg-gray-700 text-gray-300 hover:text-white hover:bg-gray-600 hover:shadow-md"
                    : "bg-gray-50 text-gray-600 hover:text-brand-dark-blue hover:bg-gray-100 hover:shadow-md"
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="space-y-6">
              <h2
                className={`text-xl font-semibold ${
                  darkMode ? "text-white" : "text-brand-dark-blue"
                }`}
              >
                Profile Information
              </h2>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="settings-name"
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      First Name
                    </label>
                    <input
                      id="settings-name"
                      type="text"
                      maxLength={50}
                      value={profileForm.name}
                      onChange={(e) =>
                        setProfileForm({
                          ...profileForm,
                          name: processNameInput(e.target.value),
                        })
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="settings-surname"
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Last Name
                    </label>
                    <input
                      id="settings-surname"
                      type="text"
                      maxLength={50}
                      value={profileForm.surname}
                      onChange={(e) =>
                        setProfileForm({
                          ...profileForm,
                          surname: processNameInput(e.target.value),
                        })
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    maxLength={254}
                    value={user?.email || ""}
                    disabled
                    className={`w-full px-4 py-3 border rounded-lg cursor-not-allowed ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-gray-400"
                        : "border-gray-300 bg-gray-50 text-gray-500"
                    }`}
                  />
                  <p
                    className={`text-sm mt-1 ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Email cannot be changed
                  </p>
                </div>

                {/* Professional Info Field - Only for Doctors */}
                {isDoctor && (
                  <div>
                    <label
                      htmlFor="settings-info"
                      className={`block text-sm font-medium mb-2 ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Professional Information
                    </label>
                    <textarea
                      id="settings-info"
                      maxLength={1000}
                      value={profileForm.info}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, info: e.target.value })
                      }
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors resize-vertical ${
                        darkMode
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "border-gray-300"
                      }`}
                      placeholder="Add your workplace, specialization, credentials, or other professional information..."
                    />
                    <p
                      className={`text-sm mt-1 ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      This information will be visible to your patients
                    </p>
                  </div>
                )}

                {/* Treatment Type Selection */}
                <div className="space-y-3">
                  <label
                    className={`block text-sm font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Treatment Mode
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setProfileForm({
                          ...profileForm,
                          treatmentType: "self",
                        });
                        setTreatmentTypeChanged(true);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        profileForm.treatmentType === "self"
                          ? "border-brand-cyan bg-brand-cyan/10 text-brand-cyan"
                          : darkMode
                            ? "border-gray-600 bg-gray-700 hover:border-gray-500"
                            : "border-gray-300 bg-white hover:border-gray-400"
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center">
                          {profileForm.treatmentType === "self" && (
                            <div className="w-2 h-2 bg-current rounded-full"></div>
                          )}
                        </div>
                        <div className="text-center">
                          <div className="font-medium">Self Treatment</div>
                          <div className="text-xs opacity-75">
                            I am the patient
                          </div>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setProfileForm({
                          ...profileForm,
                          treatmentType: "child",
                        });
                        setTreatmentTypeChanged(true);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        profileForm.treatmentType === "child"
                          ? "border-brand-cyan bg-brand-cyan/10 text-brand-cyan"
                          : darkMode
                            ? "border-gray-600 bg-gray-700 hover:border-gray-500"
                            : "border-gray-300 bg-white hover:border-gray-400"
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center">
                          {profileForm.treatmentType === "child" && (
                            <div className="w-2 h-2 bg-current rounded-full"></div>
                          )}
                        </div>
                        <div className="text-center">
                          <div className="font-medium">Child Treatment</div>
                          <div className="text-xs opacity-75">
                            My child is the patient
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">
                    Current selection: {profileForm.treatmentType}
                  </div>

                  {/* Warning when treatment type is changed */}
                  {treatmentTypeChanged && (
                    <div className="p-4 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/20">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-amber-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                            Treatment mode changed. Please press "Update
                            Profile" to save changes.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Info message for child treatment */}
                  {profileForm.treatmentType === "child" && (
                    <div className="p-4 rounded-lg border-2 border-dashed border-brand-cyan bg-brand-cyan/5">
                      <p className="text-sm text-brand-cyan font-medium">
                        💡 You can add children details in the Children tab
                        after updating your profile
                      </p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !hasProfileChanges()}
                  className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? "Updating..." : "Update Profile"}
                </button>
              </form>
            </div>
          )}

          {/* Children Tab */}
          {activeTab === "children" && currentTreatmentType === "child" && (
            <div className="space-y-6">
              <ChildManagement
                user={user}
                darkMode={darkMode}
                childrenCount={childrenCount}
                onChildrenUpdated={async () => {
                  // Only set status if we're on the children tab
                  if (activeTab === "children") {
                    setStatusWithTimeout("Children updated successfully!");
                  }
                  fetchChildrenCount();
                  // Fetch latest profile from Supabase and update parent
                  if (user) {
                    const { data: updatedProfile, error } = await supabase
                      .from("user_data")
                      .select("name, surname, account_type, treatment_type")
                      .eq("user_id", user.id)
                      .single();
                    if (!error && updatedProfile) {
                      onProfileUpdate(updatedProfile);
                    } else {
                      onProfileUpdate(profile); // fallback
                    }
                  } else {
                    onProfileUpdate(profile);
                  }
                }}
              />
            </div>
          )}

          {/* Goals Tab - Only show for self treatment type */}
          {activeTab === "goals" && profileForm.treatmentType === "self" && (
            <div className="space-y-6">
              <h2
                className={`text-xl font-semibold ${
                  darkMode ? "text-white" : "text-brand-dark-blue"
                }`}
              >
                Your Goals
              </h2>
              <form onSubmit={handleGoalsUpdate} className="space-y-4">
                <div
                  className={`text-sm ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Set your goal for each day. Enter <b>0:00</b> for a rest day.
                  Goals range from 0:30 to 8:00. Weekly goal is automatically
                  calculated.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(
                    [
                      ["Monday", "monday_goal_minutes"],
                      ["Tuesday", "tuesday_goal_minutes"],
                      ["Wednesday", "wednesday_goal_minutes"],
                      ["Thursday", "thursday_goal_minutes"],
                      ["Friday", "friday_goal_minutes"],
                      ["Saturday", "saturday_goal_minutes"],
                      ["Sunday", "sunday_goal_minutes"],
                    ] as const
                  ).map(([label, key]) => {
                    const value = goalsForm[key] ?? 0;
                    const isRestDay = value === 0 || value === null;

                    return (
                      <div key={key}>
                        <label
                          htmlFor={`settings-${key}`}
                          className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          <span>{label}</span>
                          {isRestDay && (
                            <span
                              className="text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-0.5 rounded"
                              title="Rest Day"
                            >
                              🛌 Rest Day
                            </span>
                          )}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            id={`settings-${key}-hours`}
                            type="number"
                            value={Math.floor(value / 60)}
                            onChange={(e) => {
                              let hours = parseInt(e.target.value, 10) || 0;
                              if (hours > 8) hours = 8;
                              if (hours < 0) hours = 0;
                              const currentMinutes = value % 60;
                              let total = hours * 60 + currentMinutes;
                              if (total > 480) total = 480;
                              setGoalsForm({
                                ...goalsForm,
                                [key]: total,
                              });
                            }}
                            className={`w-20 px-3 py-3 border rounded-lg text-center focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                              isRestDay
                                ? darkMode
                                  ? "bg-green-900/20 border-green-600 text-green-200"
                                  : "bg-green-50 border-green-300 text-green-800"
                                : darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                  : "border-gray-300"
                            }`}
                            placeholder="0"
                            min="0"
                            max="8"
                          />
                          <span className={`text-lg font-bold ${darkMode ? "text-gray-300" : "text-gray-600"}`}>:</span>
                          <input
                            id={`settings-${key}-minutes`}
                            type="number"
                            value={value % 60}
                            onChange={(e) => {
                              let mins = parseInt(e.target.value, 10) || 0;
                              if (mins > 59) mins = 59;
                              if (mins < 0) mins = 0;
                              const currentHours = Math.floor(value / 60);
                              let total = currentHours * 60 + mins;
                              if (total > 480) total = 480;
                              setGoalsForm({
                                ...goalsForm,
                                [key]: total,
                              });
                            }}
                            className={`w-20 px-3 py-3 border rounded-lg text-center focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                              isRestDay
                                ? darkMode
                                  ? "bg-green-900/20 border-green-600 text-green-200"
                                  : "bg-green-50 border-green-300 text-green-800"
                                : darkMode
                                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                  : "border-gray-300"
                            }`}
                            placeholder="00"
                            min="0"
                            max="59"
                          />
                          <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>h : min</span>
                        </div>
                        {isRestDay && (
                          <p
                            className={`text-xs mt-1 ${
                              darkMode ? "text-green-300" : "text-green-600"
                            }`}
                          >
                            No training goal for this day
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  type="submit"
                  disabled={
                    loading ||
                    JSON.stringify(goalsForm) === JSON.stringify(originalGoals)
                  }
                  className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? "Updating..." : "Update Goals"}
                </button>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === "security" && (
            <div className="space-y-6">
              <h2
                className={`text-xl font-semibold ${
                  darkMode ? "text-white" : "text-brand-dark-blue"
                }`}
              >
                Change Password
              </h2>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label
                    htmlFor="settings-current-password"
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Current Password
                  </label>
                  <input
                    id="settings-current-password"
                    type="password"
                    maxLength={128}
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        currentPassword: e.target.value,
                      })
                    }
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter your current password"
                  />
                </div>
                <div>
                  <label
                    htmlFor="settings-new-password"
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    New Password
                  </label>
                  <input
                    id="settings-new-password"
                    type="password"
                    maxLength={128}
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        newPassword: e.target.value,
                      })
                    }
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter your new password"
                  />
                  <p
                    className={`text-sm mt-1 ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Must be at least 8 characters with at least one number
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="settings-confirm-password"
                    className={`block text-sm font-medium mb-2 ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Confirm New Password
                  </label>
                  <input
                    id="settings-confirm-password"
                    type="password"
                    maxLength={128}
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        confirmPassword: e.target.value,
                      })
                    }
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "border-gray-300"
                    }`}
                    placeholder="Confirm your new password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !passwordForm.currentPassword ||
                    !passwordForm.newPassword ||
                    !passwordForm.confirmPassword
                  }
                  className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </form>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === "appearance" && (
            <div className="space-y-6">
              <h2
                className={`text-xl font-semibold ${
                  darkMode ? "text-white" : "text-brand-dark-blue"
                }`}
              >
                Theme Preferences
              </h2>
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className={`font-medium ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Dark Mode
                  </h3>
                  <p
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Toggle between light and dark theme
                  </p>
                </div>
                <button
                  onClick={onDarkModeToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 shadow-inner ${
                    darkMode ? "bg-brand-cyan shadow-lg" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-200 shadow-sm ${
                      darkMode ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === "danger" && (
            <div className="space-y-6">
              <h2
                className={`text-xl font-semibold ${
                  darkMode ? "text-white" : "text-brand-dark-blue"
                }`}
              >
                Danger Zone
              </h2>
              <div
                className={`p-4 border-2 border-dashed border-red-300 rounded-lg ${
                  darkMode ? "bg-red-900/20" : "bg-red-50"
                }`}
              >
                <h3
                  className={`font-semibold text-red-600 ${
                    darkMode ? "text-red-400" : ""
                  }`}
                >
                  Delete Account
                </h3>
                <p
                  className={`text-sm mt-1 mb-4 ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Permanently delete your account and all associated data. This
                  action cannot be undone.
                </p>
                <button
                  onClick={handleDeleteAccountClick}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  {loading ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Status Message */}
        {status && (
          <div
            onClick={clearStatus}
            className={`p-4 rounded-lg text-sm font-medium shadow-lg cursor-pointer transition-all duration-200 hover:opacity-80 ${
              status.includes("successfully")
                ? darkMode
                  ? "bg-green-900/80 text-green-300 border border-green-700 shadow-green-900/20"
                  : "bg-green-50 text-green-800 border border-green-200 shadow-green-200/20"
                : darkMode
                  ? "bg-red-900/80 text-red-300 border border-red-700 shadow-red-900/20"
                  : "bg-red-50 text-red-800 border border-red-200 shadow-red-200/20"
            }`}
            title="Click to dismiss"
          >
            <div className="flex items-center justify-between">
              <span>{status}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearStatus();
                }}
                className={`ml-2 text-lg leading-none ${
                  status.includes("successfully")
                    ? darkMode
                      ? "text-green-300 hover:text-green-200"
                      : "text-green-800 hover:text-green-600"
                    : darkMode
                      ? "text-red-300 hover:text-red-200"
                      : "text-red-800 hover:text-red-600"
                }`}
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="This action cannot be undone. All your data will be permanently deleted, including your profile, children, logs, goals, and all associated information."
        confirmText="DELETE MY ACCOUNT"
        confirmButtonText="Delete Account"
        cancelButtonText="Cancel"
        danger={true}
        darkMode={darkMode}
      />
    </div>
  );
};

export default Settings;
